import { KButton, KLgText700, KModalExportProgress, KText } from "@components";
import { exportAttachmentsUserService } from "@services";
import { useUser } from "@store";
import { Flex, Form, Input, ModalProps, message } from "antd";
import { FC, useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { KModalQuickAdd } from "./ModalQuickAdd";

const ContentModalContainer = styled(Flex)`
  gap: 16;
`;

export const ModalExportAttachments: FC<ModalProps> = ({ open, onCancel }) => {
  const { userInfo } = useUser();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openConfirmExport, setOpenConfirmExport] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (open && userInfo?.email) {
      form.setFieldsValue({ email: userInfo.email });
    }
  }, [open, userInfo, form]);

  const handleStartExport = useCallback(
    async (values: { email: string }) => {
      try {
        setIsSubmitting(true);
        await exportAttachmentsUserService({ email: values.email });
        setOpenConfirmExport(true);
      } catch (err: any) {
        message.error(err?.response?.data?.message || "Something went wrong");
      } finally {
        setIsSubmitting(false);
      }
    },
    []
  );

  const onOkExportProgress = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      setOpenConfirmExport(false);
      onCancel?.(e);
    },
    [onCancel]
  );

  return (
    <>
      <KModalExportProgress
        open={openConfirmExport}
        onOk={onOkExportProgress}
        title={<KLgText700>Export Attachments</KLgText700>}
      >
        <KText>
          Your export may take a little while to prepare your attachments and
          compress them into a .zip file for download. Keep an eye out for an
          email when it's done. You'll receive a link to download it.
        </KText>
      </KModalExportProgress>
      <KModalQuickAdd
        open={open}
        title="Export Attachments"
        onCancel={onCancel}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleStartExport}>
          <ContentModalContainer vertical>
            <KLgText700>Export all attachments for this profile to .zip</KLgText700>

            <KText style={{ marginTop: 16 }}>
              It may take some time for the server to prepare your attachments
              and compress them into a .zip file for download. Once the .zip
              file is ready, you will receive an email with a link to download.
            </KText>
            <KText style={{ marginTop: 16, marginBottom: 16 }}>
              Please confirm the email address where you would like to receive
              the download link.
            </KText>

            <Form.Item
              name="email"
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input size="large" placeholder="user@example.com" />
            </Form.Item>

            <Flex justify="center">
              <KButton
                htmlType="submit"
                size="large"
                style={{ width: "100%" }}
                loading={isSubmitting}
                title="Start Export"
              />
            </Flex>
          </ContentModalContainer>
        </Form>
      </KModalQuickAdd>
    </>
  );
};
